/**
 * ReadOnlyModuleBanner — T20
 *
 * Displays a themed notice when a module's license cap is READ (read-only).
 * Rendered at the top of a page/section so users understand immediately why
 * write actions are disabled.
 *
 * Usage:
 *   import { ReadOnlyModuleBanner } from '../../components/ui/ReadOnlyModuleBanner';
 *   const { isReadonly } = useModuleGuard('forms');
 *   {isReadonly && <ReadOnlyModuleBanner />}
 */

import styled from 'styled-components';
import { Lock } from 'lucide-react';

export interface ReadOnlyModuleBannerProps {
  /**
   * Override the default message.
   * Defaults to a generic "plan/license" explanation.
   */
  message?: string;
  className?: string;
  /** Forwarded to the root element so callers can query by page-specific test ID. */
  'data-testid'?: string;
}

const Banner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  background: ${({ theme }) => theme.colors.status.locked.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.locked.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
`;

const IconWrap = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.status.locked.text};
  margin-top: 1px;

  svg { width: 16px; height: 16px; }
`;

const BannerText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.locked.text};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  margin: 0;
`;

const DEFAULT_MESSAGE =
  'This feature is in read-only mode — your current plan or license does not permit changes. ' +
  'Contact your administrator to upgrade.';

export const ReadOnlyModuleBanner = ({
  message = DEFAULT_MESSAGE,
  className,
  'data-testid': testId = 'readonly-module-banner',
}: ReadOnlyModuleBannerProps) => (
  <Banner
    className={className}
    role="alert"
    aria-label="Read-only access notice"
    data-testid={testId}
  >
    <IconWrap>
      <Lock />
    </IconWrap>
    <BannerText>{message}</BannerText>
  </Banner>
);

export default ReadOnlyModuleBanner;
